import React, { useState, useEffect } from "react";

interface CacheStatus {
  version: string;
  totalCaches: number;
  timestamp: string;
  [key: string]: any;
}

const SWDebugPanel = () => {
  const [showPanel, setShowPanel] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus | null>(null);
  const [swVersion, setSwVersion] = useState<string | null>(null);

  const sendSWMessage = (message: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!navigator.serviceWorker?.controller) {
        reject(new Error("SW no disponible"));
        return;
      }

      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      setTimeout(() => reject(new Error("Timeout")), 5000);
      navigator.serviceWorker.controller.postMessage(message, [
        messageChannel.port2,
      ]);
    });
  };

  const getSWVersion = async () => {
    try {
      const response = await sendSWMessage({ type: "GET_VERSION" });
      setSwVersion(response.version);
    } catch (error) {
      console.error("Error obteniendo versión SW:", error);
    }
  };

  const getCacheStatus = async () => {
    try {
      const response = await sendSWMessage({ type: "GET_CACHE_STATUS" });
      setCacheStatus(response.cacheStatus);
    } catch (error) {
      console.error("Error obteniendo estado caché:", error);
    }
  };

  const refreshInfo = () => {
    getSWVersion();
    getCacheStatus();
  };

  const clearCache = async () => {
    try {
      await sendSWMessage({ type: "CLEAR_CACHE" });
    } catch (error) {
      console.error("Error limpiando caché:", error);
    }
  };

  // Hook useEffect siempre después de las funciones
  useEffect(() => {
    if (showPanel) {
      refreshInfo();
    }
  }, [showPanel]);

  // Retorno condicional DESPUÉS de todos los hooks
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  const styles = {
    debugButton: {
      display: "none",
      position: "fixed" as const,
      bottom: "20px",
      left: "20px",
      backgroundColor: "#6366f1",
      color: "white",
      border: "none",
      padding: "12px",
      borderRadius: "50%",
      cursor: "pointer",
      width: "50px",
      height: "50px",
      fontSize: "18px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      zIndex: 1000,
      fontFamily: "system-ui, sans-serif",
    },
    panel: {
      position: "fixed" as const,
      bottom: "80px",
      left: "20px",
      backgroundColor: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      padding: "16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
      minWidth: "300px",
      maxWidth: "400px",
      zIndex: 1000,
      fontFamily: "system-ui, sans-serif",
      fontSize: "14px",
    },
    title: {
      fontSize: "16px",
      fontWeight: "600",
      marginBottom: "12px",
      color: "#374151",
      borderBottom: "1px solid #e5e7eb",
      paddingBottom: "8px",
    },
    info: {
      fontSize: "12px",
      color: "#6b7280",
      marginBottom: "8px",
      fontFamily: "Monaco, Consolas, monospace",
      backgroundColor: "#f9fafb",
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #e5e7eb",
    },
    buttonGroup: {
      display: "flex",
      flexWrap: "wrap" as const,
      gap: "6px",
      marginTop: "12px",
    },
    button: {
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
    },
    dangerButton: {
      backgroundColor: "#dc2626",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
    },
  };

  return (
    <>
      <button
        style={styles.debugButton}
        onClick={() => setShowPanel(!showPanel)}
        title="SW Debug Panel"
      >
        ⚙️
      </button>

      {showPanel && (
        <div style={styles.panel}>
          <div style={styles.title}>🔧 SW Debug Panel</div>

          <div style={styles.info}>
            <strong>SW Versión:</strong> {swVersion || "Desconocida"}
            <br />
            <strong>Online:</strong> {navigator.onLine ? "✅ Sí" : "❌ No"}
            <br />
            <strong>SW Activo:</strong>{" "}
            {navigator.serviceWorker?.controller ? "✅ Sí" : "❌ No"}
          </div>

          {cacheStatus && (
            <div style={styles.info}>
              <strong>Cachés Activas:</strong> {cacheStatus.totalCaches}
              <br />
              <strong>Última actualización:</strong>{" "}
              {new Date(cacheStatus.timestamp).toLocaleTimeString()}
              <br />
              {Object.entries(cacheStatus)
                .filter(
                  ([key]) =>
                    key.includes("cache") ||
                    key.includes("static") ||
                    key.includes("api")
                )
                .map(([cache, count]) => (
                  <div
                    key={cache}
                    style={{ fontSize: "11px", marginTop: "2px" }}
                  >
                    📦 {cache.replace("app-dev-", "")}: {count} items
                  </div>
                ))}
            </div>
          )}

          <div style={styles.buttonGroup}>
            <button style={styles.button} onClick={refreshInfo}>
              🔄 Actualizar
            </button>
            <button
              style={styles.button}
              onClick={() => window.location.reload()}
            >
              🔃 Recargar
            </button>
            <button style={styles.dangerButton} onClick={clearCache}>
              🧹 Limpiar Caché
            </button>
            <button
              style={styles.button}
              onClick={() => {
                // Usar el mismo método que tu sistema existente
                navigator.serviceWorker?.register("/sw.js", {
                  updateViaCache: "none",
                });
              }}
            >
              🔍 Check Update
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SWDebugPanel;
